import { AlertTriangle, ExternalLink, FileX } from 'lucide-react';
import React from 'react';

interface FailedFilesDisplayProps {
  failureReasons: string[];
  onNavigateToFile?: (filePath: string) => void;
}

interface ParsedError {
  errorMessage: string;
  files: string[];
  errorType:
    | 'metadata'
    | 'missing_metadata'
    | 'file_size'
    | 'file_format'
    | 'embedding_failed'
    | 'indexing_failed'
    | 'access_denied'
    | 'quota_exceeded'
    | 'unknown';
}

const FailedFilesDisplay: React.FC<FailedFilesDisplayProps> = ({
  failureReasons,
  onNavigateToFile,
}) => {
  // Helper function to handle file navigation
  const handleFileClick = (filePath: string) => {
    if (onNavigateToFile) {
      const cleanPath = filePath.replace('s3://cts-llm-docs-bucket/', '');
      onNavigateToFile(cleanPath);
    }
  };
  const parseFailureReasons = (reasons: string[]): ParsedError[] => {
    const parsedErrors: ParsedError[] = [];

    reasons.forEach((reason) => {
      try {
        const parsedReasons = JSON.parse(reason) as string[];
        parsedReasons.forEach((errorDetail) => {
          const filesMatch = errorDetail.match(/\[Files: ([^\]]+)\]/);
          const errorMessageMatch = errorDetail.match(/^([^[]+)/);

          if (filesMatch && errorMessageMatch && filesMatch[1] && errorMessageMatch[1]) {
            const files = filesMatch[1].split(', ').map((file) => file.trim());
            const errorMsg = errorMessageMatch[1].trim().replace('Encountered error: ', '');

            let errorType: ParsedError['errorType'] = 'unknown';

            // Phân loại lỗi dựa trên nội dung thông báo
            if (
              errorMsg.includes('invalid metadata attributes') ||
              errorMsg.includes('metadata quota')
            ) {
              errorType = 'metadata';
            } else if (
              errorMsg.includes('No metadata was provided') ||
              errorMsg.includes('metadata file')
            ) {
              errorType = 'missing_metadata';
            } else if (
              errorMsg.includes('file size') ||
              errorMsg.includes('exceeds') ||
              errorMsg.includes('50 MB')
            ) {
              errorType = 'file_size';
            } else if (
              errorMsg.includes('format not supported') ||
              errorMsg.includes('unsupported format') ||
              errorMsg.includes('file type')
            ) {
              errorType = 'file_format';
            } else if (
              errorMsg.includes('embedding failed') ||
              errorMsg.includes('EMBEDDING_FAILED')
            ) {
              errorType = 'embedding_failed';
            } else if (
              errorMsg.includes('indexing failed') ||
              errorMsg.includes('INDEXING_FAILED')
            ) {
              errorType = 'indexing_failed';
            } else if (
              errorMsg.includes('access denied') ||
              errorMsg.includes('AccessDeniedException') ||
              errorMsg.includes('permission')
            ) {
              errorType = 'access_denied';
            } else if (
              errorMsg.includes('quota') ||
              errorMsg.includes('limit') ||
              errorMsg.includes('ServiceQuotaExceededException')
            ) {
              errorType = 'quota_exceeded';
            }

            parsedErrors.push({
              errorMessage: errorMsg,
              files,
              errorType,
            });
          }
        });
      } catch (e) {
        // If parsing fails, treat as unknown error
        parsedErrors.push({
          errorMessage: reason,
          files: [],
          errorType: 'unknown',
        });
      }
    });

    return parsedErrors;
  };

  const getErrorIcon = (errorType: string) => {
    switch (errorType) {
      case 'metadata':
        return <AlertTriangle className="h-4 w-4 text-orange-500" />;
      case 'missing_metadata':
        return <FileX className="h-4 w-4 text-red-500" />;
      case 'file_size':
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      case 'file_format':
        return <FileX className="h-4 w-4 text-purple-500" />;
      case 'embedding_failed':
        return <AlertTriangle className="h-4 w-4 text-red-600" />;
      case 'indexing_failed':
        return <AlertTriangle className="h-4 w-4 text-red-600" />;
      case 'access_denied':
        return <AlertTriangle className="h-4 w-4 text-red-700" />;
      case 'quota_exceeded':
        return <AlertTriangle className="h-4 w-4 text-orange-600" />;
      default:
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
    }
  };

  const getErrorColor = (errorType: string) => {
    switch (errorType) {
      case 'metadata':
        return 'border-orange-200 bg-orange-50';
      case 'missing_metadata':
        return 'border-red-200 bg-red-50';
      case 'file_size':
        return 'border-yellow-200 bg-yellow-50';
      case 'file_format':
        return 'border-purple-200 bg-purple-50';
      case 'embedding_failed':
        return 'border-red-300 bg-red-100';
      case 'indexing_failed':
        return 'border-red-300 bg-red-100';
      case 'access_denied':
        return 'border-red-400 bg-red-100';
      case 'quota_exceeded':
        return 'border-orange-300 bg-orange-100';
      default:
        return 'border-red-200 bg-red-50';
    }
  };

  const getErrorTextColor = (errorType: string) => {
    switch (errorType) {
      case 'metadata':
        return 'text-orange-800';
      case 'missing_metadata':
        return 'text-red-800';
      case 'file_size':
        return 'text-yellow-800';
      case 'file_format':
        return 'text-purple-800';
      case 'embedding_failed':
        return 'text-red-900';
      case 'indexing_failed':
        return 'text-red-900';
      case 'access_denied':
        return 'text-red-900';
      case 'quota_exceeded':
        return 'text-orange-900';
      default:
        return 'text-red-800';
    }
  };

  const parsedErrors = parseFailureReasons(failureReasons);

  if (parsedErrors.length === 0) {
    return null;
  }

  return (
    <div className="border-t pt-2">
      <div className="flex items-center gap-2 mb-3">
        <AlertTriangle className="h-4 w-4 text-red-500" />
        <p className="text-red-600 font-medium">
          Các file lỗi ({parsedErrors.reduce((total, error) => total + error.files.length, 0)} file)
        </p>
      </div>

      <div className="space-y-3 max-h-80 overflow-y-auto">
        {parsedErrors.map((error, index) => (
          <div key={index} className={`p-3 rounded-lg border ${getErrorColor(error.errorType)}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-2 flex-1">
                {getErrorIcon(error.errorType)}
                <div className="flex-1">
                  <p className={`font-medium text-sm ${getErrorTextColor(error.errorType)}`}>
                    {error.errorMessage}
                  </p>
                  {error.files.length > 0 && (
                    <p className="text-xs text-gray-600 mt-1">
                      {error.files.length} file bị ảnh hưởng
                    </p>
                  )}
                </div>
              </div>
            </div>

            {error.files.length > 0 && (
              <div className="mt-3 pt-2 border-t border-gray-200">
                <p className="text-xs font-medium text-gray-700 mb-2">Danh sách file lỗi:</p>
                <div className="max-h-32 overflow-y-auto space-y-1">
                  {error.files.map((file, fileIndex) => (
                    <div
                      key={fileIndex}
                      className="flex items-center gap-2 text-xs p-2 bg-white/50 rounded hover:bg-white/80 transition-colors"
                    >
                      <FileX className="h-3 w-3 text-red-400 flex-shrink-0" />
                      {onNavigateToFile ? (
                        <button
                          onClick={() => handleFileClick(file)}
                          className="text-blue-600 hover:text-blue-800 underline break-all font-mono text-left flex-1 flex items-center gap-1"
                          title="Nhấp để điều hướng đến file"
                        >
                          <span>{file.replace('s3://cts-llm-docs-bucket/', '')}</span>
                          <ExternalLink className="h-3 w-3 flex-shrink-0" />
                        </button>
                      ) : (
                        <span className="text-gray-700 break-all font-mono">
                          {file.replace('s3://cts-llm-docs-bucket/', '')}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default FailedFilesDisplay;
